import React, { useState, useRef, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../features/theme/context/ThemeContext';

const ImageCropModal = ({ isOpen, onClose, onConfirm, imageFile }) => {
  const { darkMode } = useTheme();
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [canvasSize] = useState(400);
  const [cropSize] = useState(300);
  const [sliderMin, setSliderMin] = useState(0.5);
  const [sliderMax, setSliderMax] = useState(3.0);

  // Load image when file changes
  useEffect(() => {
    if (imageFile && isOpen) {
      const img = new Image();
      const url = URL.createObjectURL(imageFile);
      
      img.onload = () => {
        setImage(img);
        // Calculate scale to show the ENTIRE original image in the canvas initially
        const scaleToShowFullImage = Math.min(canvasSize / img.width, canvasSize / img.height);
        // Start with full image visible (but don't make tiny images huge)
        const initialScale = Math.min(scaleToShowFullImage, 1.0);
        
        // Set slider range based on initial scale
        const minScale = Math.min(initialScale * 0.5, 0.2); // Allow zooming out further
        const maxScale = 3.0;
        setSliderMin(minScale);
        setSliderMax(maxScale);
        
        setScale(initialScale);
        // Center the image
        setPosition({
          x: (canvasSize - img.width * initialScale) / 2,
          y: (canvasSize - img.height * initialScale) / 2
        });
        URL.revokeObjectURL(url);
      };
      
      img.src = url;
      imageRef.current = img;
    }
  }, [imageFile, isOpen, canvasSize]);

  // Draw on canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    // Draw the full image first (this will be our base)
    ctx.drawImage(
      image,
      position.x,
      position.y,
      image.width * scale,
      image.height * scale
    );

    // Now create overlay that covers everything EXCEPT the circle
    ctx.save();
    
    // Fill entire canvas with dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    
    // Now "punch out" the circle to reveal the pure image underneath
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(canvasSize / 2, canvasSize / 2, cropSize / 2, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.restore();

    // Draw circle border
    ctx.strokeStyle = darkMode ? '#ffffff' : '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(canvasSize / 2, canvasSize / 2, cropSize / 2, 0, 2 * Math.PI);
    ctx.stroke();
  }, [image, position, scale, canvasSize, cropSize, darkMode]);

  // Redraw canvas when values change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = canvasRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left - position.x,
      y: e.clientY - rect.top - position.y
    });
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const newX = e.clientX - rect.left - dragStart.x;
    const newY = e.clientY - rect.top - dragStart.y;

    // Constrain movement to keep some part of the image in the crop area
    const imageWidth = image.width * scale;
    const imageHeight = image.height * scale;
    const cropX = (canvasSize - cropSize) / 2;
    const cropY = (canvasSize - cropSize) / 2;

    const minX = cropX + cropSize - imageWidth;
    const maxX = cropX;
    const minY = cropY + cropSize - imageHeight;
    const maxY = cropY;

    setPosition({
      x: Math.max(minX, Math.min(maxX, newX)),
      y: Math.max(minY, Math.min(maxY, newY))
    });
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle scale change
  const handleScaleChange = (e) => {
    const newScale = parseFloat(e.target.value);
    const scaleDiff = newScale / scale;
    
    // Adjust position to keep the image centered on the crop area
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;
    
    setPosition(prev => ({
      x: centerX - (centerX - prev.x) * scaleDiff,
      y: centerY - (centerY - prev.y) * scaleDiff
    }));
    
    setScale(newScale);
  };

  // Generate cropped image
  const getCroppedImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = cropSize;
    canvas.height = cropSize;
    const ctx = canvas.getContext('2d');

    // Calculate the source coordinates for cropping
    const cropX = (canvasSize - cropSize) / 2;
    const cropY = (canvasSize - cropSize) / 2;
    
    // Calculate what part of the original image to crop
    const sourceX = (cropX - position.x) / scale;
    const sourceY = (cropY - position.y) / scale;
    const sourceWidth = cropSize / scale;
    const sourceHeight = cropSize / scale;

    // Draw the cropped portion
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      cropSize,
      cropSize
    );

    return canvas;
  };

  // Handle confirm
  const handleConfirm = async () => {
    const croppedCanvas = getCroppedImage();
    
    // Convert canvas to blob
    croppedCanvas.toBlob((blob) => {
      // Create a new File object from the blob
      const croppedFile = new File([blob], imageFile.name, {
        type: imageFile.type,
        lastModified: Date.now()
      });
      
      onConfirm(croppedFile);
    }, imageFile.type, 0.9);
  };

  if (!isOpen) return null;

  const sliderFillPercent = ((scale - sliderMin) / (sliderMax - sliderMin)) * 100;
  const sliderBg = darkMode
    ? `linear-gradient(to right, #ffffff 0%, #ffffff ${sliderFillPercent}%, #404040 ${sliderFillPercent}%, #404040 100%)`
    : `linear-gradient(to right, #000000 0%, #000000 ${sliderFillPercent}%, #e5e5e5 ${sliderFillPercent}%, #e5e5e5 100%)`;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-gray-200 dark:border-[#2a2a2a] max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'var(--chat-font-family)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-[#2a2a2a]">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Adjust Profile Picture
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#252525] rounded-lg transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-[#999999] mb-4">
            Drag to reposition and use the slider to zoom. The circle shows how your image will appear.
          </p>

          {/* Canvas for image editing */}
          <div className="relative inline-block rounded-xl overflow-hidden border border-gray-200 dark:border-[#2a2a2a]">
            <canvas
              ref={canvasRef}
              width={canvasSize}
              height={canvasSize}
              className="cursor-move block"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>

          {/* Zoom slider */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Zoom
            </label>
            <style dangerouslySetInnerHTML={{ __html: `
              .image-crop-zoom-slider {
                -webkit-appearance: none;
                appearance: none;
              }
              .image-crop-zoom-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background: ${darkMode ? '#ffffff' : '#000000'};
                cursor: pointer;
                border: none;
              }
              .image-crop-zoom-slider::-moz-range-thumb {
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background: ${darkMode ? '#ffffff' : '#000000'};
                cursor: pointer;
                border: none;
              }
            `}} />
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              step="0.02"
              value={scale}
              onChange={handleScaleChange}
              className="image-crop-zoom-slider w-full h-2 rounded-lg cursor-pointer focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white focus:ring-offset-1"
              style={{ background: sliderBg }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200 dark:border-[#2a2a2a]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#252525] hover:bg-gray-200 dark:hover:bg-[#333] rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200 rounded-xl transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal; 